/**
 * Maps Supabase auth error messages to user-friendly messages
 */

type AuthErrorMap = Record<string, { message: string; suggestion?: string }>;

const errorMap: AuthErrorMap = {
  // Rate limits
  'email rate limit exceeded': {
    message: 'Too many email requests',
    suggestion: 'Please wait a few minutes before trying again.',
  },
  'rate limit exceeded': {
    message: 'Too many attempts',
    suggestion: 'Please wait a moment before trying again.',
  },

  // Login errors
  'invalid login credentials': {
    message: 'Invalid email or password',
    suggestion: 'Please check your credentials and try again.',
  },
  'invalid credentials': {
    message: 'Invalid email or password',
    suggestion: 'Please check your credentials and try again.',
  },
  'email not confirmed': {
    message: 'Email not verified',
    suggestion: 'Please check your inbox and click the confirmation link.',
  },

  // Signup errors
  'user already registered': {
    message: 'Account already exists',
    suggestion: 'Try signing in instead, or use a different email.',
  },
  'password should be at least 6 characters': {
    message: 'Password too short',
    suggestion: 'Please use at least 6 characters.',
  },
  'unable to validate email address: invalid format': {
    message: 'Invalid email format',
    suggestion: 'Please enter a valid email address.',
  },

  // Password reset errors
  'for security purposes, you can only request this once every 60 seconds': {
    message: 'Please wait before requesting again',
    suggestion: 'For security, we limit password reset requests. Try again in a minute.',
  },

  // Network errors
  'failed to fetch': {
    message: 'Connection error',
    suggestion: 'Please check your internet connection and try again.',
  },
  'network error': {
    message: 'Connection error',
    suggestion: 'Please check your internet connection and try again.',
  },

  // Session errors
  'refresh token not found': {
    message: 'Session expired',
    suggestion: 'Please sign in again.',
  },
  'invalid refresh token': {
    message: 'Session expired',
    suggestion: 'Please sign in again.',
  },
};

export interface FriendlyError {
  message: string;
  suggestion?: string;
  original: string;
}

/**
 * Converts a Supabase auth error to a user-friendly message
 */
export function getFriendlyAuthError(error: { message: string } | string): FriendlyError {
  const errorMessage = typeof error === 'string' ? error : error.message;
  const lowerMessage = errorMessage.toLowerCase();

  // Check for exact matches first
  for (const [key, value] of Object.entries(errorMap)) {
    if (lowerMessage.includes(key)) {
      return {
        message: value.message,
        suggestion: value.suggestion,
        original: errorMessage,
      };
    }
  }

  // Return the original message if no match found
  return {
    message: errorMessage,
    original: errorMessage,
  };
}

/**
 * Formats a friendly error for display
 */
export function formatAuthError(error: { message: string } | string): string {
  const friendly = getFriendlyAuthError(error);
  if (friendly.suggestion) {
    return `${friendly.message}. ${friendly.suggestion}`;
  }
  return friendly.message;
}
